import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import axios from "axios";
import "../styles/busyOverlay.css";

const DEFAULT_BUSY_MESSAGE = "Please wait...";
const MARKET_ANALYSIS_BUSY_MESSAGE =
  "Fetching live online market prices. This may take a few seconds.";

type BusyMode = "spinner" | "progress";

interface BusyContextType {
  isBusy: boolean;
  busyMessage: string;
  busyProgress: number;
  busyMode: BusyMode;
  startBusy: (message?: string, mode?: BusyMode) => void;
  stopBusy: (mode?: BusyMode) => void;
  runBusy: <T>(
    task: () => Promise<T>,
    message?: string,
    mode?: BusyMode,
  ) => Promise<T>;
}

const BusyContext = createContext<BusyContextType | undefined>(undefined);

export const BusyProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [busyCount, setBusyCount] = useState(0);
  const [progressBusyCount, setProgressBusyCount] = useState(0);
  const [busyMessage, setBusyMessage] = useState(DEFAULT_BUSY_MESSAGE);
  const [busyProgress, setBusyProgress] = useState(0);
  const [busyMode, setBusyMode] = useState<BusyMode>("spinner");

  const isBusy = busyCount > 0;
  const isProgressBusy = progressBusyCount > 0;
  const showBusyOverlay = isBusy || busyProgress === 100;

  const startBusy = useCallback(
    (message = DEFAULT_BUSY_MESSAGE, mode: BusyMode = "spinner") => {
      setBusyCount((prev) => prev + 1);

      if (mode === "progress") {
        setProgressBusyCount((prev) => prev + 1);
        setBusyMode("progress");
        setBusyMessage(message);
        setBusyProgress((prev) => (prev > 0 ? prev : 8));
        return;
      }

      setBusyMode((currentMode) =>
        currentMode === "progress" && progressBusyCount > 0
          ? "progress"
          : "spinner",
      );

      setBusyMessage((currentMessage) =>
        currentMessage === MARKET_ANALYSIS_BUSY_MESSAGE
          ? currentMessage
          : message,
      );
    },
    [progressBusyCount],
  );

  const stopBusy = useCallback((mode: BusyMode = "spinner") => {
    setBusyCount((prev) => Math.max(0, prev - 1));

    if (mode === "progress") {
      setProgressBusyCount((prev) => {
        const nextProgressCount = Math.max(0, prev - 1);

        if (nextProgressCount === 0) {
          setBusyProgress(100);
        }

        return nextProgressCount;
      });
    }
  }, []);

  const runBusy = useCallback(
    async <T,>(
      task: () => Promise<T>,
      message?: string,
      mode: BusyMode = "spinner",
    ): Promise<T> => {
      try {
        startBusy(message, mode);
        return await task();
      } finally {
        stopBusy(mode);
      }
    },
    [startBusy, stopBusy],
  );

  useEffect(() => {
    if (!isProgressBusy) return;

    const timer = window.setInterval(() => {
      setBusyProgress((prev) => {
        if (prev < 25) return Math.min(prev + 3, 25);
        if (prev < 50) return Math.min(prev + 2, 50);
        if (prev < 75) return Math.min(prev + 1.2, 75);
        if (prev < 90) return Math.min(prev + 0.6, 90);
        if (prev < 96) return Math.min(prev + 0.2, 96);

        return prev;
      });
    }, 700);

    return () => {
      window.clearInterval(timer);
    };
  }, [isProgressBusy]);

  useEffect(() => {
    if (busyCount !== 0 || busyProgress !== 100) return;

    const timer = window.setTimeout(() => {
      setBusyProgress(0);
      setBusyMessage(DEFAULT_BUSY_MESSAGE);
      setBusyMode("spinner");
    }, 450);

    return () => {
      window.clearTimeout(timer);
    };
  }, [busyCount, busyProgress]);

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const requestUrl = String(config.url || "");

        const isMarketAnalysisAnalyzeRequest = requestUrl.includes(
          "/market-analysis/analyze",
        );

        const mode: BusyMode = isMarketAnalysisAnalyzeRequest
          ? "progress"
          : "spinner";

        const message = isMarketAnalysisAnalyzeRequest
          ? MARKET_ANALYSIS_BUSY_MESSAGE
          : DEFAULT_BUSY_MESSAGE;

        (config as any)._busyMode = mode;

        startBusy(message, mode);

        return config;
      },
      (error) => {
        stopBusy("spinner");
        return Promise.reject(error);
      },
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        const mode = ((response.config as any)._busyMode ||
          "spinner") as BusyMode;

        stopBusy(mode);

        return response;
      },
      (error) => {
        const mode = ((error.config as any)?._busyMode ||
          "spinner") as BusyMode;

        stopBusy(mode);

        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [startBusy, stopBusy]);

  const value = useMemo(
    () => ({
      isBusy,
      busyMessage,
      busyProgress,
      busyMode,
      startBusy,
      stopBusy,
      runBusy,
    }),
    [isBusy, busyMessage, busyProgress, busyMode, startBusy, stopBusy, runBusy],
  );

  const roundedProgress = Math.round(busyProgress);
  const shouldShowProgressBar = busyMode === "progress" && isProgressBusy;

  return (
    <BusyContext.Provider value={value}>
      {children}

      {showBusyOverlay && (
        <div className="app-busy-overlay">
          <div
            className={`app-busy-box ${
              shouldShowProgressBar ? "app-busy-progress-box" : ""
            }`}
          >
            {shouldShowProgressBar ? (
              <>
                <div className="app-busy-progress-header">
                  <span>Processing</span>
                  <strong>{roundedProgress}%</strong>
                </div>

                <div className="app-busy-progress-track">
                  <div
                    className="app-busy-progress-fill"
                    style={{
                      width: `${roundedProgress}%`,
                    }}
                  />
                </div>

                <p>{busyMessage}</p>
              </>
            ) : (
              <>
                <div className="app-busy-spinner"></div>
                <p>{DEFAULT_BUSY_MESSAGE}</p>
              </>
            )}
          </div>
        </div>
      )}
    </BusyContext.Provider>
  );
};

export const useBusy = () => {
  const context = useContext(BusyContext);

  if (!context) {
    throw new Error("useBusy must be used inside BusyProvider");
  }

  return context;
};
